import { getCategories } from "@modules/azm/category.ts"
import { LadiesMain } from "@modules/global/ladies-main.ts"
import { InfoPanel } from "@modules/info-panel/InfoPanel.ts"
import { isMobile } from "@modules/util/device.ts"
import { base64Encode } from "@modules/util/string-transcoder.ts"
import { isInteger } from "lodash-es"
import { FILTER_GROUP_NAME_MAP } from "../azm/filter-group-name-map.ts"
import { getSelectedDistance } from "../azm/filter-selection/distance-select.ts"
import { filterGroupKeysToText } from "../azm/filter-selection/filter-tabs.ts"
import { getGroupedFilters } from "../azm/filter.ts"
import { getAllFilterSegmentsOldFormat } from "../azm/old-filter-format.ts"
import { getLocation } from "../location/location.ts"
import type { ApiJsonErrorMessage, UserSearchResultItem, UserSearchResults } from "../types.ts"
import { convertObjectToFormData } from "../util/convert-object-to-form-data.ts"
import { CharacterCount } from "./CharacterCount.ts"
import { fetchUserSearchResults, postNewUserSearch } from "./api.ts"

let userSearchResults: UserSearchResultItem[] = []

const userSearchDropdown = document.querySelector<HTMLElement>("#deine-suchen-dropdown")

export const userSearchContainer = () =>
    userSearchDropdown?.querySelector<HTMLUListElement>(".dropdown-content .dropdown-list")

const asJQuery = (element: HTMLElement | null | undefined) => $(element)

function createListItem(userSearchEntry: UserSearchResultItem) {
    const dropdownListItem = document.createElement("li")
    dropdownListItem.classList.add("dropdown-list-item")
    dropdownListItem.setAttribute("data-id", userSearchEntry.id)

    return dropdownListItem
}

function createItemLink(userSearchEntry: UserSearchResultItem) {
    const anchorLink = document.createElement("a")
    anchorLink.setAttribute("href", userSearchEntry.href)
    anchorLink.classList.add("link")

    return anchorLink
}

function createItemLabel(userSearchEntry: UserSearchResultItem) {
    const spanName = document.createElement("span")
    spanName.classList.add("text")
    spanName.innerText = userSearchEntry.name

    return spanName
}

export function updateUserSearchListElementsInDom() {
    if (!userSearchDropdown) {
        return
    }

    const searchContainer = userSearchContainer()
    const footerContainer = userSearchDropdown.querySelector<HTMLElement>(".dropdown-content .dropdown-footer")
    const menuContainer = userSearchDropdown
        .closest<HTMLLIElement>("li")
        ?.querySelector<HTMLAnchorElement>(".bookmark-btn-menu")
    const userSearchText = userSearchDropdown?.querySelector<HTMLElement>(".dropdown-content .dropdown-text")

    if (searchContainer) {
        searchContainer.innerText = ""
    }

    const newSearchItems: HTMLLIElement[] = []

    if (!userSearchResults.length) {
        return
    }

    for (const userSearchEntry of userSearchResults) {
        const dropdownListItem = createListItem(userSearchEntry)
        const anchorLink = createItemLink(userSearchEntry)
        const spanName = createItemLabel(userSearchEntry)

        anchorLink.appendChild(spanName)
        dropdownListItem.appendChild(anchorLink)

        newSearchItems.push(dropdownListItem)
    }

    requestAnimationFrame(() => {
        for (const newSearchItem of newSearchItems) {
            searchContainer?.appendChild(newSearchItem)
        }

        menuContainer?.classList.add("has-items")
        userSearchText?.classList.add("hidden")
        footerContainer?.classList.remove("hidden")
        searchContainer?.classList.remove("hidden")
    })
}

export function updateUserSearchModalFilterInDom() {
    const filterContainer = document.querySelector<HTMLElement>(".filter-option-label")

    if (!filterContainer) {
        return
    }

    const location = getLocation()
    const filterHtmlList = []

    // todo: check location.type to map to correct text
    if (location.slug === "standort") {
        filterHtmlList.push("Standort")
    } else if (location.plz !== "") {
        filterHtmlList.push(`<strong>PLZ: </strong>(${location.name})`)
    } else if (isInteger(location.cityId)) {
        filterHtmlList.push(`<strong>Ort: </strong>${location.name}`)
    } else {
        filterHtmlList.push(`<strong>Region: </strong>${location.name}`)
    }

    if (location.addressName !== "") {
        filterHtmlList.push(`<strong>Adresse: </strong>${location.addressName}`)
    }

    if (getCategories().length > 0) {
        filterHtmlList.push(
            `<strong>Rubrik: </strong>${getCategories()
                .map((category) => FILTER_GROUP_NAME_MAP.rubrik.values[category].text)
                .join(", ")}`,
        )
    }

    if (Object.keys(getGroupedFilters()).length > 0) {
        filterHtmlList.push(
            filterGroupKeysToText(Object.keys(getGroupedFilters()), " | ", (g: string) => `<strong>${g}:</strong> `),
        )
    }

    const distance = getSelectedDistance()
    if (distance !== "20" && distance !== "") {
        filterHtmlList.push(`<strong>Umkreis:</strong> ${distance}km`)
    }

    filterContainer.innerHTML = filterHtmlList.join(" | ")
}

const searchModalId = isMobile() ? "user-search-modal" : "user-search-save-modal"
$(() => {
    $(`#${searchModalId}`).on("show.bs.modal", () => {
        updateUserSearchModalFilterInDom()
    })
    $(`#${searchModalId}`).on("hidden.bs.modal", () => {
        LadiesMain.removeHashtagFromUrl("user-search-save-modal")
    })
})

export function registerUserSearchModalForm() {
    const searchForm = document.querySelector<HTMLFormElement>("#user-search-save-form")

    searchForm?.addEventListener("submit", async (submitEvent) => {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()

        const userSearchNameElement = document.querySelector<HTMLInputElement>("#user-search-name")
        const userSearchErrorBase = userSearchNameElement?.closest<HTMLLabelElement>("label")
        const userSearchErrorElement = userSearchErrorBase?.querySelector<HTMLSpanElement>(".user-search-name-error")
        const userSearchErrorMessageElement =
            userSearchErrorBase?.querySelector<HTMLSpanElement>(".user-search-name-message")

        const oldFiltersFormat = getAllFilterSegmentsOldFormat()
        const searchName = userSearchNameElement?.value ?? ""

        oldFiltersFormat.name = base64Encode(searchName)
        const formData = convertObjectToFormData(oldFiltersFormat)

        userSearchErrorElement?.classList.remove("show")
        if (userSearchErrorMessageElement) {
            userSearchErrorMessageElement.innerText = ""
        }

        postNewUserSearch(formData)
            .then((responseJson) => {
                const response = responseJson.newData

                siteConfig.userSearchCount = response.length
                SiteInfo.savedFirstSearch = response.savedFirstSearch

                displayModalSuccessMessage(searchName, asJQuery(searchForm))
                addEntryToSavedList(response)
            })
            .catch((error) => {
                const errorJson = JSON.parse(error.message) as ApiJsonErrorMessage
                errorJson.message ??= "Ein unbekannter Fehler ist aufgetreten"

                displayFormError(errorJson, asJQuery(userSearchErrorElement), asJQuery(userSearchErrorMessageElement))
            })
    })
}

export function setUserSearchResults(newUserSearchResults: UserSearchResults) {
    if (!newUserSearchResults.length) {
        return
    }

    siteConfig.userSearchCount = newUserSearchResults.length
    SiteInfo.savedFirstSearch = newUserSearchResults.savedFirstSearch

    userSearchResults = newUserSearchResults.entryList
}

function displayFormError(response: ApiJsonErrorMessage, errorElement: typeof $, messageElement: typeof $) {
    errorElement.addClass("show")
    messageElement.html(response.message)

    if ("hideEvent" in response && "target" in response) {
        const eventElement = errorElement.parent().find(response.target)
        eventElement.one(response.hideEvent, () => {
            errorElement.removeClass("show")
            messageElement.empty()
        })
    }
}

function displayModalSuccessMessage(saveName: string, form: typeof $) {
    const modalContainer = form.closest(".modal")
    const secureSaveName = document.createElement("div")

    secureSaveName.textContent = saveName

    modalContainer.modal("hide")
    form.length && form.get(0).reset()
    new CharacterCount()

    InfoPanel.displayMessage(`Die Suche "${secureSaveName.innerHTML}" wurde erfolgreich gespeichert!`)
}

function addEntryToSavedList(response: UserSearchResults) {
    if (!("menuDropdownListItem" in siteConfig.templates)) {
        return
    }

    const dropdownElement = $("#deine-suchen-dropdown")
    const dropdownListElement = dropdownElement.find(".dropdown-list")
    const newData = response.entryList[0]
    const mustacheData = {
        id: newData.id,
        text: newData.name,
        link: newData.href,
        label: newData.name,
    }

    const newItem = Mustache.render(siteConfig.templates.menuDropdownListItem, mustacheData)

    dropdownListElement.prepend(newItem)

    if (!siteConfig.userSearchCount) {
        siteConfig.userSearchCount = 1
    } else {
        siteConfig.userSearchCount++
    }

    if (dropdownListElement.hasClass("hidden")) {
        dropdownListElement.removeClass("hidden")
        dropdownElement.find(".dropdown-footer").removeClass("hidden")

        dropdownElement.find(".dropdown-text").addClass("hidden")
        $(".bookmark-btn-menu").addClass("has-items")
    }

    displayDropdownFirstSearchInfo(dropdownElement)
}

function displayDropdownFirstSearchInfo(dropdown: typeof $) {
    if (!SiteInfo.savedFirstSearch && siteConfig.userSearchCount > 0) {
        if ("firstUserSearchInfo" in siteConfig.templates) {
            dropdown.find(".dropdown-text").html(siteConfig.templates.firstUserSearchInfo)
            dropdown.find(".dropdown-text").removeClass("hidden")
            LadiesMain.observeUserSearchDropdownInViewport(true)
        }
    }
}
;(async () => {
    if (userSearchContainer()) {
        setUserSearchResults((await fetchUserSearchResults()).message)

        registerUserSearchModalForm()

        updateUserSearchModalFilterInDom()
        updateUserSearchListElementsInDom()
    }
})()
